// Serializable spatial-view contract (spec §3.3). This is the persisted, engine-agnostic shape a
// Spatial Explorer workspace (T16) / Dashboards Map tile stores — NOT the runtime map props
// (those live in `../map/spatialMapTypes.ts`). Kept dependency-light: string-literal unions only,
// no classes, no enums. T11–T17 + T21 consume these types.

/** Per-layer visual style. `colorByColumn` drives a categorical color ramp (T14). */
export type SpatialLayerStyle = {
  color: string;
  colorByColumn?: string;
  cluster?: boolean;
  /** T30 two-cell compare — dashed line/outline paint (e.g. `[4, 3]`). Omitted (solid) everywhere
   *  else; NOT surfaced in the Spatial Explorer layer UI (compare builds this def in-memory only). */
  lineDasharray?: readonly number[];
};

/**
 * How a layer resolves its geometry input.
 * - `column`: a real spatial column on the source.
 * - `declaration`: a MetaInfo-declared lat/lng pair or spatial column (T18) — valid on TABLE
 *   sources ONLY (a query source has no MetaInfo declaration to reference); validate on add.
 */
export type SpatialLayerGeometryRef =
  | { kind: 'column'; column: string }
  | { kind: 'declaration'; declarationId: string };

export type SpatialLayerSource =
  | { kind: 'table'; schema: string; table: string; geometryRef: SpatialLayerGeometryRef }
  | {
      kind: 'query';
      sql: string;
      geometryRef: SpatialLayerGeometryRef;
      /**
       * True when this layer's SQL exceeded `PERSISTED_SQL_LIMIT_CHARS` and was
       * dropped from the session snapshot (`sessionPersistenceCap`) — `sql` is
       * then `''`, NOT a shorter version of the query. `loadQuerySourceLayer`
       * refuses to execute such a layer and surfaces the omission on the layer
       * row instead, because a truncated query is a different query and would
       * paint the wrong rows.
       */
      sqlOmitted?: boolean;
    };

export type SpatialLayerDefinition = {
  id: string;
  label: string;
  visible: boolean;
  source: SpatialLayerSource;
  labelColumn?: string;
  style: SpatialLayerStyle;
  /** Max features materialized onto the map for this layer. Default 10_000. */
  featureCap: number;
};

export type SpatialBasemapMode = 'default' | 'none';

export type SpatialViewDefinition = {
  layers: SpatialLayerDefinition[];
  basemap: SpatialBasemapMode;
};

/** The featureCap every new layer starts with (spec §3.3). */
export const DEFAULT_SPATIAL_FEATURE_CAP = 10_000;
